/**
 * Validation. Everything here runs BEFORE a clone, a model call, or a byte of network I/O.
 *
 * ⛔ The asymmetry that matters: `sibyl.toml` is ours and a mistake there stops us. `SIBYL.md`
 * arrives from a branch anyone can push — a malformed file there only ever WARNS, because if it
 * could fail the run then every pull-request author has a one-line denial of service against their
 * own reviewer, and the natural next step is routing around the tool.
 *
 * docs/spec/config.md §6
 */

import { CREDENTIAL_SHAPES, isLoopback, SCHEMA_VERSION, type SibylConfig } from "./schema.js"

/** Follows sysexits(3) so a wrapper script can branch on the reason. */
export const EXIT = {
  OK: 0,
  GATE_FIRED: 1,
  USAGE: 64,
  DATA: 65,
  UNAVAILABLE: 69,
  CONFIG: 78,
} as const

export interface Diagnostic {
  readonly level: "error" | "warn"
  readonly message: string
  readonly where?: string
  readonly hint?: string
}

export class ConfigError extends Error {
  constructor(
    readonly diagnostics: readonly Diagnostic[],
    readonly exitCode: number = EXIT.CONFIG,
  ) {
    super(diagnostics.map((d) => d.message).join("\n"))
    this.name = "ConfigError"
  }
}

/** Levenshtein, for "did you mean". Bounded by the short keys it runs on. */
function editDistance(a: string, b: string): number {
  const prev: number[] = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0] as number
    prev[0] = i
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j] as number
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      prev[j] = Math.min((prev[j] as number) + 1, (prev[j - 1] as number) + 1, diag + cost)
      diag = tmp
    }
  }
  return prev[b.length] as number
}

/** The nearest known key, when it is near enough to be worth suggesting. */
export function nearestKey(unknown: string, known: readonly string[]): string | undefined {
  let best: string | undefined
  let bestD = Infinity
  for (const k of known) {
    const d = editDistance(unknown.toLowerCase(), k.toLowerCase())
    if (d < bestD) {
      bestD = d
      best = k
    }
  }
  // A distance over a third of the key's length is not a typo, it is a different word.
  return best !== undefined && bestD <= Math.max(2, Math.floor(unknown.length / 3)) ? best : undefined
}

/**
 * Compile a gitignore-style glob to a RegExp.
 *
 * ⛔ Every glob in the config is compiled at STARTUP, not on first use. A glob that fails open
 * silently reviews everything, and one discovered forty minutes into a review has already wasted
 * the run.
 */
export function compileGlob(glob: string, caseSensitive: boolean): RegExp {
  if (glob.trim() === "") throw new Error("empty glob")
  let re = ""
  let i = 0
  while (i < glob.length) {
    const c = glob[i] as string
    if (c === "*") {
      if (glob[i + 1] === "*") {
        // `**` crosses directory separators; `**/` also matches zero directories.
        if (glob[i + 2] === "/") {
          re += "(?:.*/)?"
          i += 3
          continue
        }
        re += ".*"
        i += 2
        continue
      }
      re += "[^/]*"
      i += 1
      continue
    }
    if (c === "?") {
      re += "[^/]"
      i += 1
      continue
    }
    if (c === "[") {
      const close = glob.indexOf("]", i + 1)
      if (close === -1) throw new Error(`unterminated character class in ${JSON.stringify(glob)}`)
      let body = glob.slice(i + 1, close)
      if (body.startsWith("!")) body = "^" + body.slice(1)
      re += `[${body}]`
      i = close + 1
      continue
    }
    re += c.replace(/[.+^${}()|[\]\\]/g, "\\$&")
    i += 1
  }
  return new RegExp(`^${re}$`, caseSensitive ? "" : "i")
}

/**
 * Every check from docs/spec/config.md §6.
 *
 * Returns warnings; THROWS `ConfigError` on anything fatal, so a caller cannot accidentally
 * continue past a bad config by ignoring a return value.
 */
export function validate(cfg: SibylConfig, rawText?: string): Diagnostic[] {
  const errors: Diagnostic[] = []
  const warnings: Diagnostic[] = []

  if (cfg.schemaVersion > SCHEMA_VERSION) {
    errors.push({
      level: "error",
      message: `config schema_version ${cfg.schemaVersion} is newer than this build supports (${SCHEMA_VERSION})`,
      hint: "A file written by a newer build has keys we would silently drop. Upgrade sibyl, or pin the file to this version.",
    })
  }

  // ⛔ Secrets never live in the config file. Name the key and the line; never echo the value.
  if (rawText !== undefined) {
    const lines = rawText.split("\n")
    lines.forEach((line, idx) => {
      if (line.trimStart().startsWith("#")) return
      for (const shape of CREDENTIAL_SHAPES) {
        if (shape.test(line)) {
          const key = line.split("=")[0]?.trim() ?? "(unknown)"
          errors.push({
            level: "error",
            message: `credential-shaped value in the config file, at key \`${key}\``,
            where: `sibyl.toml:${idx + 1}`,
            hint: "Credentials belong in the credential store and are referred to here by NAME only. The value has not been echoed.",
          })
          break
        }
      }
    })
  }

  if (!isLoopback(cfg.server.bind) && !cfg.server.allowNonLoopback) {
    errors.push({
      level: "error",
      message: `server.bind is ${JSON.stringify(cfg.server.bind)}, which is not loopback`,
      hint: "This surface has no authentication and can post to a shared repository. Set server.allow_non_loopback = true only if you genuinely mean it.",
    })
  }

  if (cfg.secrets.readback !== "never") {
    errors.push({
      level: "error",
      message: `secrets.readback must be "never" — it is written down so the guarantee is legible, not so it can be traded away`,
    })
  }

  const enabled = cfg.dimensions.filter((d) => d.enabled)
  if (enabled.length === 0 && cfg.validation.emptyDimensions === "error") {
    errors.push({
      level: "error",
      message: "no dimension is enabled — that is not a configuration, it is a mistake",
    })
  }

  const seen = new Map<string, number>()
  cfg.dimensions.forEach((d, idx) => {
    // A backstop only. Duplicates inside one file are rejected during the merge, where both blocks
    // are still visible; by the time we get here they have collapsed into one. This catches a
    // duplicate introduced by a caller constructing a config programmatically.
    const prior = seen.get(d.id)
    if (prior !== undefined) {
      errors.push({
        level: "error",
        message: `duplicate dimension id ${JSON.stringify(d.id)} (blocks ${prior + 1} and ${idx + 1})`,
      })
    }
    seen.set(d.id, idx)

    if (!/^[a-z0-9_-]+$/.test(d.id)) {
      errors.push({
        level: "error",
        message: `dimension id ${JSON.stringify(d.id)} must match [a-z0-9_-]+`,
        hint: "The id is a stable key in the cache, the triage database and comment anchors.",
      })
    }

    const hasFile = d.promptFile !== undefined && d.promptFile !== ""
    const hasInline = d.prompt !== undefined && d.prompt !== ""
    if (hasFile === hasInline) {
      errors.push({
        level: "error",
        message: `dimension ${JSON.stringify(d.id)} needs exactly one of prompt_file or prompt, ${hasFile ? "both are set" : "neither is set"}`,
        hint: "Two sources of truth for a prompt is how you debug the wrong text.",
      })
    }

    for (const [field, globs] of [
      ["include", d.include],
      ["exclude", d.exclude],
    ] as const) {
      globs.forEach((g) => {
        try {
          compileGlob(g, cfg.paths.caseSensitive)
        } catch (e) {
          errors.push({
            level: "error",
            message: `dimension ${JSON.stringify(d.id)} has an invalid ${field} glob ${JSON.stringify(g)}: ${(e as Error).message}`,
          })
        }
      })
    }
  })

  // ⛔ Compile every path glob now, not on first use.
  for (const [field, globs] of [
    ["paths.include", cfg.paths.include],
    ["paths.exclude", cfg.paths.exclude],
  ] as const) {
    globs.forEach((g) => {
      try {
        compileGlob(g, cfg.paths.caseSensitive)
      } catch (e) {
        errors.push({
          level: "error",
          message: `invalid glob in ${field}: ${JSON.stringify(g)} — ${(e as Error).message}`,
        })
      }
    })
  }

  const compiledOverrides: RegExp[][] = []
  cfg.paths.overrides.forEach((o, idx) => {
    const compiled: RegExp[] = []
    o.match.forEach((g) => {
      try {
        compiled.push(compileGlob(g, cfg.paths.caseSensitive))
      } catch (e) {
        errors.push({
          level: "error",
          message: `invalid glob in paths.override[${idx}]: ${JSON.stringify(g)} — ${(e as Error).message}`,
        })
      }
    })
    compiledOverrides.push(compiled)

    if (o.dimensions !== undefined) {
      for (const id of o.dimensions) {
        if (!seen.has(id)) {
          warnings.push({
            level: "warn",
            message: `paths.override[${idx}] names dimension ${JSON.stringify(id)}, which is not defined`,
          })
        }
      }
    }
  })

  // A later override whose pattern set is identical shadows an earlier one entirely.
  for (let i = 0; i < cfg.paths.overrides.length; i++) {
    for (let j = i + 1; j < cfg.paths.overrides.length; j++) {
      const a = cfg.paths.overrides[i]?.match ?? []
      const b = cfg.paths.overrides[j]?.match ?? []
      if (a.length > 0 && a.length === b.length && a.every((g) => b.includes(g))) {
        warnings.push({
          level: "warn",
          message: `paths.override[${i}] is fully shadowed by paths.override[${j}] — last match wins, so the earlier block has no effect`,
        })
      }
    }
  }

  for (const lvl of cfg.severity.levels) {
    if (cfg.severity.actions[lvl] === undefined) {
      errors.push({
        level: "error",
        message: `severity level ${JSON.stringify(lvl)} has no action block — a level with no mapping is a finding class with undefined behaviour`,
      })
    }
  }

  if (cfg.post.enabled && cfg.triage.requireHuman) {
    warnings.push({
      level: "warn",
      message:
        "posting is enabled; every finding still requires triage acceptance before it is posted",
      hint: "These are complementary gates, not a contradiction. This line is informational.",
    })
  }

  if (cfg.diff.onExceed === "truncate" || cfg.diff.onExceed === "skip") {
    warnings.push({
      level: "warn",
      message: `diff.on_exceed = ${JSON.stringify(cfg.diff.onExceed)} produces a review that looks complete over a diff that was not read`,
      hint: "Every finding and the summary will carry a coverage warning. `chunk` is the honest option.",
    })
  }

  if (cfg.repo.allowInPlaceReview) {
    warnings.push({
      level: "warn",
      message:
        "repo.allow_in_place_review is on — parallel sessions can mutate the tree underneath a review, and findings may cite lines that were never in the pull request",
      hint: "Every finding from this run will be stamped `in-place`.",
    })
  }

  if (errors.length > 0) throw new ConfigError([...errors, ...warnings])
  return warnings
}
